import { useCallback, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { PdfFieldRect, PdfFieldSample, PdfNormalizedRect, PdfOverlayRender, PdfSource } from "./types";
import { PdfDocumentViewer } from "./PdfDocumentViewer";
import { PdfSamplePreview } from "./PdfSamplePreview";
import { PdfFieldOverlay } from "./PdfFieldOverlay";
import { PdfFieldCanvas } from "./PdfFieldCanvas";

type WorkbenchMode = "edit" | "preview";

interface PdfTemplateWorkbenchProps {
  source: PdfSource | string | null;
  initialFields?: PdfFieldRect[];
  initialSamples?: PdfFieldSample[];
  width?: number;
}

function createFieldId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `field_${Date.now()}`;
}

export function PdfTemplateWorkbench({
  source,
  initialFields = [],
  initialSamples = [],
  width = 600,
}: PdfTemplateWorkbenchProps) {
  const [mode, setMode] = useState<WorkbenchMode>("edit");
  const [currentPage, setCurrentPage] = useState(1);
  const [fields, setFields] = useState<PdfFieldRect[]>(initialFields);
  const [selectedId, setSelectedId] = useState<string | null>(initialFields[0]?.id ?? null);
  const [sampleValues, setSampleValues] = useState<Record<string, string>>(() =>
    initialSamples.reduce<Record<string, string>>((acc, sample) => {
      acc[sample.id] = sample.value ?? "";
      return acc;
    }, {}),
  );

  const selectedField = fields.find((field) => field.id === selectedId) ?? null;

  const handleCreateRect = useCallback(
    (page: number, rect: PdfNormalizedRect) => {
      const id = createFieldId();
      const newField: PdfFieldRect = {
        id,
        page,
        rect,
        meta: {
          label: `Field ${fields.length + 1}`,
        },
      };

      setFields((prev) => [...prev, newField]);
      setSampleValues((prev) => ({ ...prev, [id]: "" }));
      setSelectedId(id);
    },
    [fields.length],
  );

  const handleRemoveField = useCallback((id: string) => {
    setFields((prev) => prev.filter((field) => field.id !== id));
    setSampleValues((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
    setSelectedId((prev) => (prev === id ? null : prev));
  }, []);

  const handleUpdateLabel = useCallback((id: string, label: string) => {
    setFields((prev) =>
      prev.map((field) =>
        field.id === id
          ? {
              ...field,
              meta: {
                ...field.meta,
                label,
              },
            }
          : field,
      ),
    );
  }, []);

  const handleSampleChange = useCallback((id: string, value: string) => {
    setSampleValues((prev) => ({ ...prev, [id]: value }));
  }, []);

  const sampleList = useMemo<PdfFieldSample[]>(() => {
    return Object.entries(sampleValues).map(([id, value]) => ({
      id,
      value,
    }));
  }, [sampleValues]);

  const overlayRenderer = useCallback<PdfOverlayRender>(
    ({ containerRef, ...metrics }) => {
      const pageFields = fields.filter((field) => field.page === metrics.page);
      return (
        <>
          <PdfFieldOverlay
            fields={pageFields}
            metrics={metrics}
            selectedId={selectedId}
            onSelect={(field) => setSelectedId(field.id)}
          />
          {mode === "edit" ? (
            <PdfFieldCanvas
              metrics={metrics}
              fields={pageFields}
              onCreateRect={(rect) => handleCreateRect(metrics.page, rect)}
              onSelectField={(field) => setSelectedId(field?.id ?? null)}
            />
          ) : null}
        </>
      );
    },
    [fields, handleCreateRect, mode, selectedId],
  );

  return (
    <div className="flex h-full w-full flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant={mode === "edit" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("edit")}
          >
            Edit Fields
          </Button>
          <Button
            variant={mode === "preview" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("preview")}
            disabled={fields.length === 0}
          >
            Preview Values
          </Button>
        </div>
        <div className="text-xs text-muted-foreground">
          {selectedField ? `${selectedField.meta?.label ?? selectedField.id} on page ${selectedField.page}` : "No field selected"}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 lg:flex-row">
        <div className="w-full shrink-0 space-y-4 rounded-md border bg-card p-4 shadow lg:w-72 lg:max-w-72">
          <div>
            <h3 className="text-sm font-semibold text-card-foreground">Fields</h3>
            <p className="text-xs text-muted-foreground">
              Select a field to update its label or sample value.
            </p>
          </div>

          <div className="flex max-h-[420px] flex-col gap-3 overflow-y-auto pr-1">
            {fields.length === 0 ? (
              <div className="rounded border border-dashed p-4 text-center text-xs text-muted-foreground">
                Draw rectangles on the PDF to add fields.
              </div>
            ) : (
              fields.map((field) => {
                const label = (field.meta as any)?.label ?? field.id;
                return (
                  <div
                    key={field.id}
                    className={cn(
                      "space-y-2 rounded-md border p-2 text-xs",
                      selectedId === field.id && "border-primary shadow-sm",
                    )}
                    onClick={() => setSelectedId(field.id)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-card-foreground">{label}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRemoveField(field.id);
                        }}
                      >
                        ×
                      </Button>
                    </div>
                    <div className="text-muted-foreground">Page {field.page}</div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase text-muted-foreground">Label</Label>
                      <Input
                        value={label}
                        onChange={(event) => handleUpdateLabel(field.id, event.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase text-muted-foreground">Sample Value</Label>
                      <Input
                        value={sampleValues[field.id] ?? ""}
                        onChange={(event) => handleSampleChange(field.id, event.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="flex min-h-[500px] flex-1 flex-col rounded-md border bg-card shadow">
          {mode === "edit" ? (
            <PdfDocumentViewer
              source={source}
              width={width}
              page={currentPage}
              onPageChange={setCurrentPage}
              renderOverlay={overlayRenderer}
            />
          ) : (
            <PdfSamplePreview
              source={source}
              fields={fields}
              samples={sampleList}
              width={width}
              page={currentPage}
              onPageChange={setCurrentPage}
              renderOverlay={overlayRenderer}
            />
          )}
        </div>
      </div>
    </div>
  );
}

